/**
 * ProductTour
 *
 * Main orchestrator for the tour system.  Rendered via createPortal directly
 * onto document.body so it sits above all app content.
 *
 * Responsibilities:
 *  - Lock body scroll while a tour is active
 *  - Dismiss tour on Escape key
 *  - Dismiss tour when user clicks outside the tooltip AND outside the
 *    highlighted target element
 *  - Compose TourSpotlight + TourTooltip
 */

import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTour } from '@/contexts/TourContext';
import { TourSpotlight } from './TourSpotlight';
import { TourTooltip } from './TourTooltip';

export function ProductTour() {
  const {
    currentTour,
    currentStep,
    isActive,
    nextStep,
    previousStep,
    skipTour,
    dismissTour,
  } = useTour();

  const step = currentTour?.steps[currentStep];

  // ── Lock body scroll ──────────────────────────────────────────────────────
  useEffect(() => {
    if (isActive) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isActive]);

  // ── Escape key dismissal ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissTour();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isActive, dismissTour]);

  // ── Click-outside dismissal ───────────────────────────────────────────────
  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!isActive || !step) return;

      const target = e.target as Node;

      // Check if click is inside the tooltip
      const tooltip = document.querySelector('[data-tour-tooltip]');
      if (tooltip?.contains(target)) return;

      // Check if click is inside the highlighted DOM element
      if (step.target) {
        const highlighted = document.querySelector(step.target);
        if (highlighted?.contains(target)) return;
      }

      dismissTour();
    },
    [isActive, step, dismissTour],
  );

  useEffect(() => {
    if (!isActive) return;
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isActive, handleMouseDown]);

  if (!isActive || !currentTour || !step) return null;

  return createPortal(
    <>
      <TourSpotlight step={step} isActive={isActive} />
      <TourTooltip
        step={step}
        currentStep={currentStep}
        totalSteps={currentTour.steps.length}
        onNext={nextStep}
        onPrevious={previousStep}
        onSkip={skipTour}
        onClose={dismissTour}
      />
    </>,
    document.body,
  );
}
